import React, { useCallback, useRef, useState } from 'react'
import { GETArrayResponse, RecentlyOpenedEntity, ResultObjectType } from '@cbgms/api'
import { mapValueToFilterArray } from '@cbgms/components/src/top-search/topSearchUtils'
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query'
import { useGetSilentSearchErrors } from 'app/search/hooks/useFilterSilentSearchErrors'
import { useSearch } from 'app/search/hooks/useSearch'
import { useMappedSearchResults } from './hooks/useMappedSearchResults'
import { usePlaceholderText } from './hooks/usePlaceholderText'
import { ITopSearchContextExpose, TopSearchContext } from '@cbgms/components/src/top-search/context/TopSearchContext'
import { useFetchRecentlyOpenedEntities } from 'app/search/hooks/use-fetch-recently-opened-entities'
import { useFormatRecentlyOpenedResults } from './hooks/useRecentlyOpenedResults'
import { useSearchMenuOptions } from './hooks/use-search-menu-options'
import { useGetVehicle } from '../../../vehicles/hooks/useGetVehicle'
import { ResultComponentSelector } from '@cbgms/components/src/top-search/search-v2/ResultsV2/ResultComponentSelectorV2'
import { SearchResultTypeContextProvider } from './SearchResultTypeContextProvider'
import { useNoResultsPlaceholderText } from 'app/menu/components/Search/hooks/use-no-results-placeholder-text'
import { useSearchCategoryFilter } from 'app/menu/components/Search/hooks/use-search-category-filter'
import { useFeature } from '@cbgms/base/feature-flags'

export const TopSearchContextProvider: React.FC = ({ children }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const onClose = useCallback(() => setOpen(false), [setOpen])
  const menuOptions = useSearchMenuOptions()
  const hasB2BSearchOptimization = useFeature('b2b-search-optimization')

  const { searchFilter, setSearchFilter, showSearchFilter } = useSearchCategoryFilter()
  const searchPlaceholder = usePlaceholderText(searchFilter)
  const noResultsPlaceholder = useNoResultsPlaceholderText()

  const searchTypes = React.useMemo(() => mapValueToFilterArray(searchFilter), [searchFilter])
  const searchTypesForTopSearch = React.useMemo(
    () => (hasB2BSearchOptimization ? searchTypes.filter(s => s !== ResultObjectType.Parts) : searchTypes),
    [searchTypes, hasB2BSearchOptimization]
  )

  const { loading, query, setQuery, searchResponse } = useSearch(searchTypesForTopSearch)
  const refetchSearch = (searchResponse as any)?.refetch

  const queryClient = useQueryClient()
  const clearQuery = React.useCallback(() => {
    setQuery('')
    onClose()
  }, [setQuery, onClose, queryClient])

  const results = useMappedSearchResults(searchResponse, query, searchTypesForTopSearch, clearQuery, ResultComponentSelector, false)
  const silentErrors = useGetSilentSearchErrors(searchResponse)
  const isRecentEntitiesEnabled = open && query.trim().length === 0
  const recentEntitiesQuery = useFetchRecentlyOpenedEntities(isRecentEntitiesEnabled)
  const recentlyOpenedEntities = useFormatRecentlyOpenedResults(recentEntitiesQuery, clearQuery, setQuery, refetchSearch, inputRef)
  const refetchRecentlyOpenedEntities = useCallback(() => {
    return recentEntitiesQuery.refetch({ cancelRefetch: false }).then(result => {
      const response = result.data as GETArrayResponse<RecentlyOpenedEntity> | undefined

      if (response && Array.isArray(response.Data)) {
        return result as QueryObserverResult<GETArrayResponse<RecentlyOpenedEntity>>
      }

      throw new Error('Unexpected response type')
    })
  }, [recentEntitiesQuery])
  const GetVehicleData = (uuid: string) => {
    const result = useGetVehicle(uuid)
    return { data: result.data, isLoading: result.isLoading }
  }
  const providerData: ITopSearchContextExpose = {
    showSearchFilter,
    searchFilter,
    setSearchFilter,
    menuOptions,
    searchPlaceholder,
    noResultsPlaceholder,
    loading,
    results,
    clearQuery,
    setQuery,
    query,
    silentErrors,
    recentlyOpenedEntities,
    refetchRecentlyOpenedEntities,
    onClose,
    open,
    setOpen,
    GetVehicleData
  }

  return (
    <TopSearchContext.Provider value={providerData}>
      <SearchResultTypeContextProvider>{children}</SearchResultTypeContextProvider>
    </TopSearchContext.Provider>
  )
}
